/**
 * SAT HTC™ · Client Journey (M3)
 * ------------------------------------------------------------------
 * Ablauf (nicht als Aufgabenliste, sondern als Weg):
 *   AURA-Erkenntnis → Mein Transformationsziel → Mein Weg
 *   → Heute ausprobieren → Was geschah wirklich? → Lernen
 *   → nächste Anwendung → Integration
 *
 * Die 5 Movements DECODE → FEEL → RECODE → EMBODY → SERVE erscheinen
 * als sichtbare HTC-Reise, aber NICHT als starres Level-System.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("sat_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MOVEMENT_LABELS = {
  DECODE: "Erkennen",
  FEEL:   "Fühlen",
  RECODE: "Neu wählen",
  EMBODY: "Verkörpern",
  SERVE:  "Beitragen",
};

export default function HtcJourney() {
  const [consentGranted, setConsentGranted] = useState(false);
  const [sessionState,   setSessionState] = useState(null);
  const [sessionId,      setSessionId] = useState(null);
  const [targets,        setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [practices,      setPractices] = useState([]);
  const [journeyState,   setJourneyState] = useState([]);
  const [error,          setError] = useState("");
  const [pdfLoading,     setPdfLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/htc/session/current`, { headers: authHeaders() });
        setSessionId(r.data.session_id);
        setSessionState(r.data.state);
        if (r.data.session_id) await refreshJourney();
      } catch (e) {
        // 503 wenn HTC_MODULE_ENABLED=false — nichts weiter tun
        setError(e?.response?.data?.detail?.message || "HTC ist derzeit nicht verfügbar.");
      }
    })();
  }, []);

  async function refreshJourney() {
    try {
      const [tgs, jrn] = await Promise.all([
        axios.get(`${API}/api/htc/targets/eligible`, { headers: authHeaders() }),
        axios.get(`${API}/api/htc/journey/current`, { headers: authHeaders() }),
      ]);
      setTargets(tgs.data.targets);
      setJourneyState(jrn.data.targets);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  async function grantConsent() {
    await axios.post(`${API}/api/htc/consent`, { accepted: true }, { headers: authHeaders() });
    setConsentGranted(true);
  }

  async function startSession() {
    try {
      const r = await axios.post(`${API}/api/htc/session`, {}, { headers: authHeaders() });
      setSessionId(r.data.session_id);
      setSessionState("OPEN");
      await refreshJourney();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  async function loadPractices(t) {
    setSelectedTarget(t);
    const r = await axios.get(`${API}/api/htc/targets/${t.target_id}/practices/eligible`,
      { headers: authHeaders() });
    setPractices(r.data.practices);
  }

  async function confirmTarget(t, form) {
    await axios.post(`${API}/api/htc/targets/${t.target_id}/confirm`, form,
      { headers: authHeaders() });
    await refreshJourney();
  }

  async function rejectTarget(t) {
    await axios.post(`${API}/api/htc/targets/${t.target_id}/reject`,
      { reason: "NOT_READY", free_text: "" }, { headers: authHeaders() });
    await refreshJourney();
  }

  async function releaseSession() {
    setPdfLoading(true);
    try {
      await axios.post(`${API}/api/htc/session/${sessionId}/release`, {},
        { headers: authHeaders() });
      const tk = await axios.post(`${API}/api/htc/reports/current/token`, {},
        { headers: authHeaders() });
      window.open(`${API}/api/htc/reports/download/${tk.data.token}`, "_blank");
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setPdfLoading(false);
  }

  const eligibleOnly = useMemo(
    () => targets.filter(t => t.state === "SYSTEM_ELIGIBLE"),
    [targets]
  );
  const activeOnly = useMemo(
    () => journeyState.filter(t => ["ACTIVE", "IN_PRACTICE", "MENTOR_REVIEWED",
      "CLIENT_SELECTED"].includes(t.state)),
    [journeyState]
  );

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-slate-100" data-testid="htc-journey-page">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <p className="mb-2 text-xs uppercase tracking-widest text-slate-400">SAT HTC™</p>
        <h1 className="mb-8 text-4xl font-light tracking-tight text-white sm:text-5xl">
          Mein Transformationsweg
        </h1>
        <p className="mb-10 max-w-xl text-slate-400">
          Aus deiner AURA-Auswertung entstehen konkrete Möglichkeiten. Du entscheidest,
          wo du hinschauen willst — nicht das System.
        </p>

        {error && (
          <div className="mb-6 rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
               data-testid="htc-error">
            {error}
          </div>
        )}

        {/* Consent */}
        {!consentGranted && !sessionId && (
          <ConsentCard onGrant={grantConsent} />
        )}

        {/* Session starten */}
        {(consentGranted || sessionState) && !sessionId && (
          <button onClick={startSession}
                  data-testid="htc-start-session-btn"
                  className="rounded bg-sky-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-sky-400">
            Meine Möglichkeiten aus AURA öffnen
          </button>
        )}

        {/* Eligible Targets */}
        {sessionId && eligibleOnly.length > 0 && (
          <section className="mt-10" data-testid="htc-eligible-section">
            <h2 className="mb-3 text-lg font-medium text-slate-100">Vorgeschlagene Wege</h2>
            <p className="mb-6 text-sm text-slate-400">
              Aus deinen Mustern ergeben sich diese möglichen Ansatzpunkte. Wähle, was
              dich anspricht — oder lehne ab.
            </p>
            <div className="space-y-4">
              {eligibleOnly.map(t => (
                <TargetProposal key={t.target_id} t={t}
                                onConfirm={confirmTarget}
                                onReject={rejectTarget} />
              ))}
            </div>
          </section>
        )}

        {/* Aktive Reise */}
        {activeOnly.length > 0 && (
          <section className="mt-12" data-testid="htc-active-section">
            <h2 className="mb-3 text-lg font-medium text-slate-100">Meine aktuelle Reise</h2>
            {activeOnly.map(t => (
              <ActiveTargetCard key={t.target_id} t={t}
                                 onOpenPractices={() => loadPractices(t)}
                                 selected={selectedTarget?.target_id === t.target_id}
                                 practices={selectedTarget?.target_id === t.target_id ? practices : []} />
            ))}
          </section>
        )}

        {/* Release */}
        {sessionId && activeOnly.length > 0 && (
          <button onClick={releaseSession} disabled={pdfLoading}
                  data-testid="htc-release-session-btn"
                  className="mt-10 rounded border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50">
            {pdfLoading ? "Erstelle Plan…" : "Meinen Transformation Plan als PDF"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Sub-Components -------------------------------------------------------
function ConsentCard({ onGrant }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6"
         data-testid="htc-consent-card">
      <h2 className="mb-2 text-lg text-white">Einwilligung · HTC Preview</h2>
      <p className="mb-4 text-sm text-slate-400">
        HTC ist ein strukturierter Entwicklungsweg — keine Therapie, keine Diagnose.
        Deine Reflexionen bleiben mit dir und deiner Mentor:in.
      </p>
      <button onClick={onGrant} data-testid="htc-consent-accept-btn"
              className="rounded bg-emerald-500 px-5 py-2 text-sm text-white hover:bg-emerald-400">
        Einwilligen und starten
      </button>
    </div>
  );
}

function TargetProposal({ t, onConfirm, onReject }) {
  const [current, setCurrent]   = useState("");
  const [desired, setDesired]   = useState("");
  const [why,     setWhy]       = useState("");
  const [signal,  setSignal]    = useState("");
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5"
         data-testid={`htc-target-proposal-${t.source_breakpoint_id}`}>
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
        <span>{t.htc_code_ref}</span>
        <span>·</span>
        <span>{MOVEMENT_LABELS[t.htc_movement_ref] || t.htc_movement_ref}</span>
        <span>·</span>
        <span>{t.domain}</span>
      </div>
      <p className="mb-4 text-slate-300">Ein möglicher Weg — du entscheidest, ob er zu dir passt.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <textarea placeholder="Heute: so reagiere ich" value={current}
                   onChange={e => setCurrent(e.target.value)}
                   data-testid="htc-target-current"
                   className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
        <textarea placeholder="Stattdessen: so möchte ich reagieren" value={desired}
                   onChange={e => setDesired(e.target.value)}
                   data-testid="htc-target-desired"
                   className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
        <textarea placeholder="Warum: was bedeutet mir das?" value={why}
                   onChange={e => setWhy(e.target.value)}
                   data-testid="htc-target-why"
                   className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
        <textarea placeholder="Woran merke ich Veränderung?" value={signal}
                   onChange={e => setSignal(e.target.value)}
                   data-testid="htc-target-signal"
                   className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
      </div>
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onConfirm(t, {
            target_definition: why.slice(0, 500),
            current_response:  current.slice(0, 2000),
            desired_response:  desired.slice(0, 2000),
            success_signal:    signal.slice(0, 500),
            client_priority:   1,
          })}
          data-testid="htc-target-confirm-btn"
          className="rounded bg-sky-500 px-4 py-1.5 text-sm text-white hover:bg-sky-400">
          Diesen Weg bestätigen
        </button>
        <button onClick={() => onReject(t)}
                data-testid="htc-target-reject-btn"
                className="rounded border border-slate-600 px-4 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
          Passt für mich nicht
        </button>
      </div>
    </div>
  );
}

function ActiveTargetCard({ t, onOpenPractices, selected, practices }) {
  const [reflection, setReflection] = useState("");
  const [status, setStatus] = useState("");

  async function recordReflection() {
    try {
      await axios.post(`${API}/api/htc/reflections`, {
        target_id: t.target_id, content: reflection,
        change_signal_flag: true,
      }, { headers: authHeaders() });
      await axios.post(`${API}/api/htc/signals/change`, {
        target_id: t.target_id, note: reflection.slice(0, 250),
      }, { headers: authHeaders() });
      setStatus("Reflexion festgehalten. Danke.");
      setReflection("");
    } catch (e) {
      setStatus(e?.response?.data?.detail?.message || String(e));
    }
  }

  return (
    <div className="mb-4 rounded-2xl border border-slate-800 bg-slate-900/40 p-5"
         data-testid={`htc-active-target-${t.target_id}`}>
      <div className="mb-2 text-xs uppercase tracking-widest text-slate-400">
        {t.htc_code_ref} · Fokus: {MOVEMENT_LABELS[t.htc_movement_ref]} · {t.state}
      </div>
      <p className="mb-3 text-slate-100">{t.target_definition || "Ziel noch nicht formuliert."}</p>
      <div className="mb-3 grid grid-cols-2 gap-2 text-xs text-slate-400 sm:grid-cols-4">
        <div>Beobachtete Veränderungen: <span className="text-slate-100">{t.change_signal_count}</span></div>
        <div>Mentor bestätigt: <span className="text-slate-100">{t.mentor_validated_count}</span></div>
        <div>Integrationssignale: <span className="text-slate-100">{t.integration_signal_count}</span></div>
        <div>Praxis-Instanzen: <span className="text-slate-100">{t.practice_count}</span></div>
      </div>

      <button onClick={onOpenPractices}
              data-testid="htc-load-practices-btn"
              className="mb-3 text-xs text-sky-300 hover:underline">
        Praxis-Möglichkeiten anzeigen
      </button>

      {selected && practices.length > 0 && (
        <div className="mb-3 space-y-1 rounded border border-slate-700 bg-slate-950/40 p-3 text-sm">
          {practices.map(p => (
            <div key={p.practice_id} data-testid={`htc-practice-${p.practice_id}`}
                 className="text-slate-200">
              • <strong>{p.name}</strong> · {p.estimated_effort}
              {p.mentor_review_required && (
                <span className="ml-2 text-xs text-amber-300">mentor-begleitet</span>
              )}
            </div>
          ))}
        </div>
      )}

      <textarea placeholder="Was ist heute wirklich passiert? — kurz notieren."
                 value={reflection}
                 onChange={e => setReflection(e.target.value)}
                 data-testid="htc-reflection-textarea"
                 className="mb-2 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
      <button onClick={recordReflection}
              data-testid="htc-record-reflection-btn"
              className="rounded bg-slate-700 px-4 py-1.5 text-sm text-slate-100 hover:bg-slate-600">
        Reflexion + Change Signal festhalten
      </button>
      {status && <p className="mt-2 text-xs text-slate-400">{status}</p>}
    </div>
  );
}
